import { get, isEqual } from "lodash";
import Cropper from "cropperjs";
import { MediaTransform, thor } from "../../typings/thor";

export class MediaEditorController {
    public static $inject: string[] = [
        "$log",
        "$mdDialog",
        "$mdMedia",
        "$scope",
        "$timeout",
        "$translate",
        "mediaService",
        "rewriteUrlImg",
        "toaster"
    ];

    // Binded
    public media;
    public onUpdate;
    public resourceId: number;

    public isImageBusy: boolean;
    public isUncroppedImageLoad: boolean;
    public isToneOpen: boolean;
    public thumborMediaUrl;
    public transform: MediaTransform;
    public uncroppedThumborMediaUrl;

    private cropper;
    private currentView;
    private initialTransform: MediaTransform;
    private previousCropDatas;
    private tempCropValues;

    private $t;

    constructor(
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        public $mdMedia: ng.material.IMedia,
        private $scope: ng.IScope,
        private $timeout: ng.ITimeoutService,
        $translate: ng.translate.ITranslateService,
        private mediaService,
        private rewriteUrlImg,
        private toaster
    ) {
        this.$t = $translate.instant;
    }

    public $onInit() {
        this.currentView = "default";
        this.isUncroppedImageLoad = false;
        this.isToneOpen = false;
        this.transform = {
            brightness: get(this.media, "meta.transform.brightness", 0),
            contrast: get(this.media, "meta.transform.contrast", 0),
            saturation: get(this.media, "meta.transform.saturation", 1),
            sharpen: {
                amount: get(this.media, "meta.transform.sharpen.amount", 0),
                radius: get(this.media, "meta.transform.sharpen.radius", 0)
            },
            crop: get(this.media, "meta.transform.crop", null),
            rotate: get(this.media, "meta.transform.rotate", 0)
        };
        this.initialTransform = angular.copy(this.transform);

        this.$scope.$watch(
            () => this.transform,
            () => {
                this.thumborMediaUrl = this._generateImage();
                this.uncroppedThumborMediaUrl = this._generateUncroppedImage();
            },
            true
        );
    }

    public $onChanges(changes) {
        if (changes.media) {
            this.media = angular.copy(changes.media.currentValue);
        }
    }

    public applyCrop() {
        this.cropper.destroy();
        this.transform.crop = this.tempCropValues;
        this._setWrapperSize("image-to-display");
        this.currentView = "default";
    }

    public close() {
        if (this.hasMetaChanged()) {
            const confirm = this.$mdDialog
                .confirm()
                .title(this.$t("fermer_sans_sauvegarder"))
                .textContent(this.$t("vous_allez_perdre_vos_modifications"))
                .ariaLabel(this.$t("fermer_l_editeur_de_media_sans_sauvegarder"))
                .ok(this.$t("oui"))
                .cancel(this.$t("annuler"));

            this.$mdDialog.show(confirm).then(
                () => this._close(),
                () => true
            );
        } else {
            this._close();
        }
    }

    public hasMetaChanged() {
        return !isEqual(this.initialTransform, this.transform);
    }

    public onImageLoad() {
        this.isImageBusy = false;
        this._setWrapperSize("image-to-display");
    }

    public onUncroppedImageLoad() {
        this.isUncroppedImageLoad = true;
    }

    public toggleTonality() {
        this.currentView = this.currentView === "default" ? "tone" : "default";
        this.isToneOpen = !this.isToneOpen;
    }

    public reset() {
        if (this.currentView === "crop") {
            this.cropper.destroy();
            this.tempCropValues = null;
            this.currentView = "default";
            this._setWrapperSize("image-to-display");
        } else {
            this.transform = angular.copy(this.initialTransform);
        }
    }

    public rotate(direction) {
        if (direction === "left") {
            if (this.transform.rotate === 270) {
                this.transform.rotate = 0;
            } else {
                this.transform.rotate += 90;
            }
        } else {
            if (this.transform.rotate === -270) {
                this.transform.rotate = 0;
            } else {
                this.transform.rotate -= 90;
            }
        }
    }

    public startCrop() {
        this.isImageBusy = true;
        this.currentView = "crop";
        this.isToneOpen = false;

        this._setWrapperSize("image-to-crop");

        const image = <HTMLImageElement>document.getElementById("image-to-crop");
        this._startCrop(image);
    }

    public saveMeta() {
        const mediaId = this.media.id;
        const meta = { transform: this.transform };
        this.mediaService
            .updateMedia(mediaId, { meta })
            .then(() => {
                this.initialTransform = angular.copy(this.transform);
                this.onUpdate({ $event: meta });
            })
            .catch(error => {
                this.$log.error("Error while saving media transforms", error);
                this.toaster.toast({ message: this.$t("une_erreur_est_survenue_veuillez_reessayer") });
            });
    }

    public removeCrop() {
        const confirm = this.$mdDialog
            .confirm()
            .title(this.$t("souhaitez_vous_supprimer_le_recadrage"))
            .textContent(this.$t("vous_retrouverez_le_recadrage_de_l_image"))
            .ariaLabel(this.$t("confirmation_suppression_du_crop"))
            .ok(this.$t("supprimer_le_recadrage"))
            .cancel(this.$t("annuler"));

        this.$mdDialog.show(confirm).then(
            () => {
                this.reset();
                this.transform.crop = null;
            },
            () => true
        );
    }

    private _close() {
        const closeAnimationDuration = 250;

        angular
            .element(document.querySelector(".media-editor"))
            .removeClass("media-editor--animation-open")
            .addClass("media-editor--animation-close");

        this.$timeout(() => {
            this.$scope.$destroy();
            angular.element(document.querySelector("media-editor")).remove();
        }, closeAnimationDuration);
    }

    private _generateImage(uncroppedVersion = false) {
        const transform = angular.copy(this.transform);

        if (!uncroppedVersion) {
            this.isImageBusy = true;
        } else {
            delete transform.crop;
            delete transform.rotate;
        }

        const rewriteUrlImgParameters: thor.RewriteUrlImgParameters = {
            transform: transform
        };

        const newThumborUrl = this.rewriteUrlImg.buildUrl(this.media.url, rewriteUrlImgParameters);

        if (!uncroppedVersion && newThumborUrl === this.thumborMediaUrl) {
            this.isImageBusy = false;
        }

        return newThumborUrl;
    }

    private _generateUncroppedImage() {
        return this._generateImage(true);
    }

    /**
     * CropperJS a besoin que l'image soit dans un wrapper avec une taille définis
     * exemple:
     * <div class="wrapper">
     *     <img src="emma.jpg" />
     * </div>
     *
     * Nous avons besoin que la taille du wrapper soit égale à celle de l'image
     * pour que la zone de crop ne dépasse pas.
     *
     * En pure CSS il est impossible de définir une taille d'un bloc
     * selon une image à taille dynamique à l'intérieur.
     *
     * De plus, nous avons besoin de connaître le ratio de l'image et de la résolution
     * du block contenant le wrapper (ici wrapperGrandParent) afin que l'image
     * ne dépasse pas de ce wrapper et conserve son ratio.
     *
     * C'est pour cela qu'on définis la width ou la height à auto (selon le ratio),
     * afin que l'image ne soit pas déformée.
     *
     * Ici, on défini une marge de 5% du wrapperGrandParent.
     *
     * @param imageId, Id CSS de l'image sur laquelle est basé la taille du wrapper
     */
    private _setWrapperSize(imageId) {
        const $image = angular.element(document.getElementById(imageId));
        const image = <HTMLImageElement>$image[0];
        const wrapper = <HTMLDivElement>document.querySelector(".media-editor__image-wrapper");
        const wrapperGrandParent = <HTMLDivElement>wrapper.parentElement.parentElement;
        const imageSizeRatio = image.naturalWidth / image.naturalHeight;
        const wrapperGrandParentSizeRatio = wrapperGrandParent.clientWidth / wrapperGrandParent.clientHeight;

        if (imageSizeRatio <= wrapperGrandParentSizeRatio) {
            if (image.naturalHeight <= wrapperGrandParent.clientHeight) {
                wrapper.style.height = image.naturalHeight + "px";
            } else {
                wrapper.style.height = wrapperGrandParent.clientHeight * 0.9 + "px";
            }
            wrapper.style.width = "auto";
        } else {
            if (image.naturalWidth <= wrapperGrandParent.clientWidth) {
                wrapper.style.width = image.naturalWidth + "px";
            } else {
                wrapper.style.width = wrapperGrandParent.clientWidth * 0.9 + "px";
            }
            wrapper.style.height = "auto";
        }
    }

    private _startCrop(image: HTMLImageElement) {
        this.cropper = new Cropper(image, {
            viewMode: 1,
            checkCrossOrigin: false,
            data: this.previousCropDatas,
            dragMode: <any>"move",
            crop: e => {
                this.previousCropDatas = e.detail;
                /**
                 * ThumborUrlBuilder need values to be strictly positives
                 * and superior to 0 (that's why we add a + 1)
                 */
                this.tempCropValues = {
                    left: Math.round(Math.abs(e.detail.x)) + 1,
                    top: Math.round(Math.abs(e.detail.y)) + 1,
                    right: Math.round(Math.abs(e.detail.x)) + Math.round(Math.abs(e.detail.width)) + 1,
                    bottom: Math.round(Math.abs(e.detail.y)) + Math.round(Math.abs(e.detail.height)) + 1
                };
            },
            ready: () => {
                this.$scope.$apply(() => {
                    this.isImageBusy = false;
                });
            }
        });
    }
}
